import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterBar from '@/components/feature/FilterBar';
import Pagination from '@/components/base/Pagination';
import MovieCard from '@/components/base/MovieCard';
import { useSeo } from '@/hooks/useSeo';
import { applyFilters, allGenres, getGenreByKey, getMoviesByGenre, type FilterOptions } from '@/utils/movies';

const PAGE_SIZE = 8;

export default function Category() {
  const { genre = 'action' } = useParams<{ genre: string }>();
  const genreInfo = getGenreByKey(genre);
  const [filters, setFilters] = useState<FilterOptions>({ sort: 'hot' });
  const [page, setPage] = useState(1);

  const genreName = genreInfo?.name ?? '全部影片';

  useSeo({
    title: `【${genreName}电影】在线观看 - 6080yy电影在线看`,
    description: `6080yy电影在线看${genreName}频道，汇集丰富的${genreName}资源，涵盖不同地区与年代的优秀作品，支持在线观看与便捷筛选，满足你的观影需求。`,
    keywords: `${genreName},${genreName}电影在线观看,6080yy电影在线看,高清电影`,
  });

  useEffect(() => {
    setPage(1);
  }, [genre]);

  const filtered = useMemo(() => {
    const base = genreInfo ? getMoviesByGenre(genre) : [];
    return applyFilters(base, filters);
  }, [genre, genreInfo, filters]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleFilterChange = (next: FilterOptions) => {
    setFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ sort: 'hot' });
    setPage(1);
  };

  if (!genreInfo) {
    return (
      <div className="w-full px-4 md:px-6 py-16 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-300 w-14 h-14 flex items-center justify-center mx-auto"></i>
        <h1 className="mt-4 font-heading font-bold text-xl text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在或已被移除。</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-300"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-foreground-300"></i>
        <span className="text-foreground-900">{genreName}</span>
      </nav>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <span className="w-14 h-14 rounded-lg bg-primary-500 flex items-center justify-center shrink-0">
          <i className={`${genreInfo.icon} text-accent-500 text-2xl w-7 h-7 flex items-center justify-center`}></i>
        </span>
        <div>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">{genreName}电影</h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-3xl leading-relaxed">{genreInfo.desc}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-5 -mx-4 px-4 md:mx-0 md:px-0">
        {allGenres.map((item) => (
          <Link
            key={item.key}
            to={`/category/${item.key}`}
            className={`shrink-0 px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
              item.key === genre
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {item.name}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <FilterBar value={filters} onChange={handleFilterChange} />
        </aside>

        <section>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-foreground-500">
              共 <span className="text-accent-600 font-heading font-bold">{filtered.length}</span> 部影片
            </p>
          </div>

          {currentItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
                {currentItems.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination current={page} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="py-16 text-center bg-background-50 rounded-lg border border-background-200">
              <i className="ri-movie-line text-5xl text-foreground-300 w-14 h-14 flex items-center justify-center mx-auto"></i>
              <h2 className="mt-4 font-heading font-bold text-lg text-foreground-950">暂无符合筛选条件的电影</h2>
              <p className="mt-2 text-sm text-foreground-500">试试调整或重置筛选条件，发现更多精彩影片。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                一键重置筛选
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}